import type { ReactNode } from 'react';

export type BadgeVariant = 'status' | 'watch';

export interface BadgeProps {
  readonly children: ReactNode;
  /** Show a leading dot. Shape depends on variant. */
  readonly dot?: boolean;
  /**
   * `status` (default) — neutral pill, round dot in currentColor.
   * `watch` — link-indigo pill, square dot.
   */
  readonly variant?: BadgeVariant;
  readonly className?: string;
}

/**
 * Status pill used for node states (PoC, Inference, Down, On) and for
 * marking watch-only wallet rows.
 *
 * Always neutral for status variant. Watch variant is the only time
 * Badge uses a non-neutral color (link indigo) because it signals a
 * category of wallet, not a state.
 *
 * 9px uppercase, 0.5 letter-spacing, weight 600.
 */
export function Badge({
  children,
  dot = false,
  variant = 'status',
  className = '',
}: BadgeProps) {
  const variantClass =
    variant === 'watch' ? 'text-link' : 'text-text-secondary';
  const dotShape =
    variant === 'watch' ? 'h-1 w-1 rounded-[1px]' : 'h-1 w-1 rounded-full';
  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full bg-surface-2 px-2 py-0.5',
        'text-[9px] font-semibold uppercase tracking-[0.5px]',
        variantClass,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {dot && (
        <span
          data-badge-dot
          aria-hidden
          className={[dotShape, 'bg-current'].join(' ')}
        />
      )}
      {children}
    </span>
  );
}
